<template>
  <div class="login-container">
    <Card class="login-card">
      <template #header>
        <div class="login-header">
          <h2 class="text-center mb-0">
            Login to AutoDenoise
          </h2>
        </div>
      </template>
      
      <template #content>
        <form @submit.prevent="handleLogin" class="login-form">
          <div class="form-group mb-3">
            <label for="username" class="form-label">Username</label>
            <input
              id="username"
              v-model="formData.username"
              type="text"
              class="form-control"
              placeholder="Enter your username"
              required
              :disabled="isLoading"
            />
          </div>

          <div class="form-group mb-3">
            <label for="password" class="form-label">Password</label>
            <input
              id="password"
              v-model="formData.password"
              type="password"
              class="form-control"
              placeholder="Enter your password"
              required
              :disabled="isLoading"
            />
          </div>

          <div v-if="errorMessage" class="alert alert-danger mb-3">
            <i class="pi pi-exclamation-triangle me-2"></i>
            {{ errorMessage }}
          </div>

          <Button
            type="submit"
            :loading="isLoading"
            :disabled="!isFormValid"
            label="Login"
            icon="pi pi-sign-in"
            class="w-100 mb-3"
            severity="primary"
          />

          <div class="text-center">
            <span class="text-muted">Don't have an account? </span>
            <a href="#" @click.prevent="$emit('switch-to-register')" class="text-primary">
              Register here
            </a>
          </div>
        </form>
      </template>
    </Card>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'Login',
  emits: ['login-success', 'switch-to-register'],
  
  data() {
    return {
      formData: {
        username: '',
        password: ''
      },
      isLoading: false,
      errorMessage: ''
    }
  },

  computed: {
    isFormValid() {
      return this.formData.username.trim().length >= 3 && 
             this.formData.password.length >= 6
    }
  },

  methods: {
    getApiBaseUrl() {
      // For production, use the deployed API endpoint with /api prefix
      if (process.env.NODE_ENV === 'production') {
        return `${window.location.protocol}//${window.location.hostname}/api`
      }

      // For development, always use localhost:3000 (backend server) with /api prefix
      const protocol = window.location.protocol
      const hostname = window.location.hostname
      const port = '3000'

      // If accessing via localhost, keep localhost (for development on same machine)
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return `${protocol}//localhost:${port}/api`
      }

      // For mobile or other devices, use the actual IP address with port 3000
      return `${protocol}//${hostname}:${port}/api`
    },

    async handleLogin() {
      if (!this.isFormValid) return

      this.isLoading = true
      this.errorMessage = ''

      try {
        const response = await axios.post(`${this.getApiBaseUrl()}/auth/login`, {
          username: this.formData.username.trim(),
          password: this.formData.password
        })

        if (response.data.token) {
          // Store token in localStorage
          localStorage.setItem('authToken', response.data.token)
          localStorage.setItem('user', JSON.stringify(response.data.user))
          
          this.$emit('login-success', {
            token: response.data.token,
            user: response.data.user
          })
        }

      } catch (error) {
        console.error('Login error:', error)
        
        if (error.response && error.response.data && error.response.data.error) {
          this.errorMessage = error.response.data.error
        } else {
          this.errorMessage = 'Login failed. Please check your connection and try again.'
        }
      } finally {
        this.isLoading = false
      }
    },

    clearForm() {
      this.formData.username = ''
      this.formData.password = ''
      this.errorMessage = ''
    }
  },

  mounted() {
    // Focus on username field when component mounts
    this.$nextTick(() => {
      const usernameInput = document.getElementById('username')
      if (usernameInput) {
        usernameInput.focus()
      }
    })
  }
}
</script>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem;
}

.login-card {
  width: 100%;
  max-width: 400px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.login-header {
  color: black;
  padding-top: 4rem;
  border-radius: 8px 8px 0 0;
  margin: -1.5rem -1.5rem 0 -1.5rem;
}

.login-form {
  padding: 1rem;
}

.form-label {
  font-weight: 600;
  color: var(--text-color);
  margin-bottom: 0.5rem;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.form-control:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb), 0.1);
}

.form-control:disabled {
  background-color: #f8f9fa;
  opacity: 0.6;
}

.alert {
  padding: 0.75rem;
  border-radius: 6px;
  border: 1px solid transparent;
}

.alert-danger {
  color: #721c24;
  background-color: #f8d7da;
  border-color: #f5c6cb;
}

@media (max-width: 768px) {
  .login-container {
    padding: 1rem;
  }
  
  .login-header {
    padding: 1.5rem;
  }
}
</style>
